// Region geometry helpers for the Konva timeline.
//
// Phase 4-5 will flesh these out with binary-search visible-range lookups,
// no-overlap drag clamping, and multi-region delta application. For now we
// expose the function signatures so consumers can import.

/**
 * Binary-search lower bound: returns the first index `i` in `arr` such that
 * `keyOf(arr[i]) >= value`. Assumes `arr` is sorted ascending by `keyOf`.
 *
 * @template T
 * @param {T[]} arr
 * @param {number} value
 * @param {(item: T) => number} keyOf
 * @returns {number}
 */
export function lowerBound(arr, value, keyOf) {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (keyOf(arr[mid]) < value) lo = mid + 1;
        else hi = mid;
    }
    return lo;
}

/**
 * Binary-search upper bound: first index `i` such that `keyOf(arr[i]) > value`.
 *
 * @template T
 * @param {T[]} arr
 * @param {number} value
 * @param {(item: T) => number} keyOf
 * @returns {number}
 */
export function upperBound(arr, value, keyOf) {
    let lo = 0;
    let hi = arr.length;
    while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (keyOf(arr[mid]) <= value) lo = mid + 1;
        else hi = mid;
    }
    return lo;
}

/**
 * Return the slice of events whose start time falls within `[startSec, endSec]`,
 * assuming the array is sorted by start time. Also returns the index of the
 * first match so callers can map back to the original array if needed.
 *
 * @param {{start:number}[]} sortedEvents
 * @param {number} startSec
 * @param {number} endSec
 * @returns {{ slice: {start:number}[], firstIndex: number }}
 */
export function visibleSlice(sortedEvents, startSec, endSec) {
    if (!Array.isArray(sortedEvents) || sortedEvents.length === 0) {
        return { slice: [], firstIndex: 0 };
    }
    const firstIndex = lowerBound(sortedEvents, startSec, (e) => e.start);
    const lastIndex = upperBound(sortedEvents, endSec, (e) => e.start);
    return { slice: sortedEvents.slice(firstIndex, lastIndex), firstIndex };
}

/**
 * Clamp a desired `[start, end]` for a single region so it does not overlap
 * its immediate prev/next neighbors (by time), respecting a minimum frame gap.
 * Neighbors are passed in as plain `{ start, end }` objects or `null` when
 * none.
 *
 * @param {object} input
 * @param {number} input.start
 * @param {number} input.end
 * @param {{start:number,end:number}|null} input.prev
 * @param {{start:number,end:number}|null} input.next
 * @param {number} [input.frameGapSec]
 * @returns {{ start: number, end: number }}
 */
export function clampToNeighbors({ start, end, prev, next, frameGapSec = 0 }) {
    let s = start;
    let e = end;
    if (prev && Number.isFinite(prev.end)) {
        const lowerBoundForStart = prev.end + frameGapSec;
        if (s < lowerBoundForStart) {
            const delta = lowerBoundForStart - s;
            s += delta;
            e += delta;
        }
    }
    if (next && Number.isFinite(next.start)) {
        const upperBoundForEnd = next.start - frameGapSec;
        if (e > upperBoundForEnd) {
            const delta = e - upperBoundForEnd;
            e -= delta;
            s -= delta;
        }
    }
    // Final defensive check: keep duration non-negative.
    if (e < s) e = s;
    return { start: s, end: e };
}
